import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import PublicPageLayout from '../components/PublicPageLayout';
import SEOHead from '../components/SEOHead';
import { buildReferralStructuredData } from './referral-landing/referral-schemas';
import ReferralBenefits from './referral-landing/ReferralBenefits';
import ReferralTrust from './referral-landing/ReferralTrust';
import ReferralForm from './referral-landing/ReferralForm';
import ReferralFAQ from './referral-landing/ReferralFAQ';

export default function ReferralLanding() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const refCode = searchParams.get('ref_code') || '';
  const [submitted, setSubmitted] = useState(false);

  // Redirect if no refCode
  useEffect(() => {
    if (!refCode) {
      navigate('/', { replace: true });
    }
  }, [refCode, navigate]);

  if (submitted) {
    return (
      <PublicPageLayout>
        <SEOHead
          title={t('referral.landing.metaTitleSuccess')}
          description={t('referral.landing.metaDescriptionSuccess')}
          url={`https://proposai.ca/refer?ref_code=${refCode}`}
          noIndex={true}
        />
        <div className="min-h-screen flex items-center justify-center px-4">
          <Card className="max-w-md w-full text-center">
            <CardContent className="pt-8 pb-8 space-y-4">
              <CheckCircle className="mx-auto h-12 w-12 text-green-500" />
              <h2 className="text-xl font-semibold text-foreground">
                {t('referral.landing.successTitle')}
              </h2>
              <p className="text-muted-foreground text-sm">
                {t('referral.landing.successBody')}
              </p>
              <Button onClick={() => navigate('/register')} className="w-full">
                {t('referral.landing.successCta')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </PublicPageLayout>
    );
  }

  return (
    <PublicPageLayout>
      <SEOHead
        title={t('referral.landing.metaTitle')}
        description={t('referral.landing.metaDescription')}
        url={`https://proposai.ca/refer?ref_code=${refCode}`}
        structuredData={buildReferralStructuredData(t)}
        noIndex={true}
      />
      <div className="min-h-screen flex items-center justify-center px-4 py-12">
        <div className="max-w-lg w-full space-y-6">
          <ReferralBenefits />
          <ReferralTrust />
          <ReferralForm refCode={refCode} onSuccess={() => setSubmitted(true)} />
          <ReferralFAQ />
        </div>
      </div>
    </PublicPageLayout>
  );
}
